/**
 * Screen Wake Lock so a tablet does not sleep while a walk is open or in kiosk mode. Feature
 * detected and fully guarded: no support, a refusal (battery saver, hidden page) or a lock the
 * system took back are all silent. The lock is re-requested when the page becomes visible again
 * (browsers release it whenever the page is hidden).
 */

export interface WakeLockSentinelLike {
  released?: boolean;
  release(): Promise<void>;
  addEventListener?(type: "release", listener: () => void): void;
}

export interface WakeLockNavigator {
  wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> };
}

export interface WakeLockDocument {
  visibilityState?: string;
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
}

export class ScreenWakeLock {
  private wanted = false;
  private sentinel: WakeLockSentinelLike | null = null;
  private pending = false;

  private readonly nav: WakeLockNavigator;
  private readonly doc: WakeLockDocument;

  constructor(nav: WakeLockNavigator, doc: WakeLockDocument) {
    this.nav = nav;
    this.doc = doc;
    this.doc.addEventListener("visibilitychange", this.onVisibility);
  }

  get supported(): boolean {
    return typeof this.nav.wakeLock?.request === "function";
  }

  /** Holds the lock while wanted; releases it when not. Safe to call repeatedly. */
  async setWanted(wanted: boolean): Promise<void> {
    this.wanted = wanted;
    if (wanted) await this.acquire();
    else await this.drop();
  }

  dispose(): void {
    this.doc.removeEventListener("visibilitychange", this.onVisibility);
    void this.setWanted(false);
  }

  private readonly onVisibility = (): void => {
    if (this.wanted && this.doc.visibilityState === "visible") void this.acquire();
  };

  private async acquire(): Promise<void> {
    if (!this.supported || this.pending) return;
    if (this.sentinel && this.sentinel.released !== true) return;
    if (this.doc.visibilityState === "hidden") return; // re-acquired on visibilitychange
    this.pending = true;
    try {
      const sentinel = await this.nav.wakeLock!.request("screen");
      if (!this.wanted) {
        await sentinel.release().catch(() => undefined); // no longer wanted while the request was in flight
        return;
      }
      this.sentinel = sentinel;
      sentinel.addEventListener?.("release", () => {
        if (this.sentinel === sentinel) this.sentinel = null;
      });
    } catch {
      this.sentinel = null; // refused: playing goes on, the screen may sleep
    } finally {
      this.pending = false;
    }
  }

  private async drop(): Promise<void> {
    const sentinel = this.sentinel;
    this.sentinel = null;
    try {
      await sentinel?.release();
    } catch {
      // Already released.
    }
  }
}
